import { useMemo, useState } from 'react';
import type { MealType } from '@vitalai/health-models';
import { scaleFoodToGrams, searchFoods, type FoodDatabaseItem } from '@vitalai/nutrition-core';
import { analyzeFoodPhoto, uploadFoodPhoto } from '../services/apiClient';
import { useHealthData } from '../services/HealthDataContext';
import { useDataSourceMode } from '../services/useDataSourceMode';

const MEAL_TYPE_OPTIONS: Array<{ value: MealType; label: string }> = [
  { value: 'breakfast', label: 'Desayuno' },
  { value: 'lunch', label: 'Almuerzo' },
  { value: 'dinner', label: 'Cena' },
  { value: 'snack', label: 'Snack' },
];

type EntryMode = 'search' | 'photo';

interface EditableDetectedItem {
  name: string;
  grams: number;
  caloriesPerGram: number;
  proteinPerGram: number;
  carbsPerGram: number;
  fatPerGram: number;
  confidence: number;
}

function defaultMealTypeForNow(): MealType {
  const hour = new Date().getHours();
  if (hour < 11) return 'breakfast';
  if (hour < 16) return 'lunch';
  if (hour < 21) return 'dinner';
  return 'snack';
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function AddMealForm() {
  const { todayKey, logMeal } = useHealthData();
  const [mode] = useDataSourceMode();

  const [entryMode, setEntryMode] = useState<EntryMode>('search');
  const [mealType, setMealType] = useState<MealType>(defaultMealTypeForNow);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // --- Busqueda manual ---
  const [query, setQuery] = useState('');
  const [selectedFood, setSelectedFood] = useState<FoodDatabaseItem | null>(null);
  const [grams, setGrams] = useState(100);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

  // --- Foto + IA (Sprint 6) ---
  const [analysisFile, setAnalysisFile] = useState<File | null>(null);
  const [analysisPreviewUrl, setAnalysisPreviewUrl] = useState<string | null>(null);
  const [analysisPhotoKey, setAnalysisPhotoKey] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [detectedItems, setDetectedItems] = useState<EditableDetectedItem[] | null>(null);

  const results = useMemo(() => (query.trim() ? searchFoods(query).slice(0, 6) : []), [query]);
  const preview = selectedFood ? scaleFoodToGrams(selectedFood, grams) : null;
  const mealTypeLabel = (value: MealType) => MEAL_TYPE_OPTIONS.find((option) => option.value === value)?.label.toLowerCase();

  const selectFood = (food: FoodDatabaseItem) => {
    setSelectedFood(food);
    setGrams(food.defaultServingGrams);
    setQuery(food.name);
    setSavedMessage(null);
  };

  const resetSearch = () => {
    setSelectedFood(null);
    setQuery('');
    setPhotoFile(null);
    setPhotoPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
  };

  const resetAnalysis = () => {
    setDetectedItems(null);
    setAnalysisPhotoKey(null);
    setAnalysisFile(null);
    setAnalysisPreviewUrl((url) => {
      if (url) URL.revokeObjectURL(url);
      return null;
    });
  };

  const switchEntryMode = (next: EntryMode) => {
    setEntryMode(next);
    setError(null);
    setSavedMessage(null);
    resetSearch();
    resetAnalysis();
  };

  const handlePhotoChange = (file: File | null) => {
    setPhotoFile(file);
    setPhotoPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleAnalysisFileChange = (file: File | null) => {
    setAnalysisFile(file);
    setDetectedItems(null);
    setAnalysisPhotoKey(null);
    setError(null);
    setAnalysisPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleAnalyze = async () => {
    if (!analysisFile) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      const { photoKey } = await uploadFoodPhoto(analysisFile);
      setAnalysisPhotoKey(photoKey);
      const result = await analyzeFoodPhoto(photoKey);
      if (result.foods.length === 0) {
        setError('No se detecto ningun alimento en la foto. Proba con otra imagen o buscalo manualmente.');
        setDetectedItems(null);
        return;
      }
      setDetectedItems(
        result.foods.map((food) => ({
          name: food.name,
          grams: food.estimatedGrams,
          caloriesPerGram: food.estimatedGrams > 0 ? food.calories / food.estimatedGrams : 0,
          proteinPerGram: food.estimatedGrams > 0 ? food.protein / food.estimatedGrams : 0,
          carbsPerGram: food.estimatedGrams > 0 ? food.carbs / food.estimatedGrams : 0,
          fatPerGram: food.estimatedGrams > 0 ? food.fat / food.estimatedGrams : 0,
          confidence: food.confidence,
        })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const updateDetectedGrams = (index: number, nextGrams: number) => {
    setDetectedItems((items) => items?.map((item, i) => (i === index ? { ...item, grams: Math.max(nextGrams, 0) } : item)) ?? null);
  };

  const updateDetectedName = (index: number, name: string) => {
    setDetectedItems((items) => items?.map((item, i) => (i === index ? { ...item, name } : item)) ?? null);
  };

  const removeDetectedItem = (index: number) => {
    setDetectedItems((items) => items?.filter((_, i) => i !== index) ?? null);
  };

  const handleSaveSearch = async () => {
    if (!selectedFood || !preview) return;
    setIsSaving(true);
    setError(null);
    try {
      // La foto solo se sube de verdad en modo Cloudflare (hay un backend real
      // al que subirla); en modo demo queda solo como preview local.
      const photoKey = mode === 'cloudflare' && photoFile ? (await uploadFoodPhoto(photoFile)).photoKey : undefined;

      await logMeal({
        date: todayKey,
        mealType,
        time: new Date().toISOString(),
        photoKey,
        items: [{ name: selectedFood.name, grams, ...preview }],
      });

      setSavedMessage(`${selectedFood.name} agregado a ${mealTypeLabel(mealType)}.`);
      resetSearch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDetected = async () => {
    if (!detectedItems || detectedItems.length === 0) return;
    setIsSaving(true);
    setError(null);
    try {
      await logMeal({
        date: todayKey,
        mealType,
        time: new Date().toISOString(),
        photoKey: analysisPhotoKey ?? undefined,
        items: detectedItems.map((item) => ({
          name: item.name,
          grams: item.grams,
          calories: Math.round(item.grams * item.caloriesPerGram),
          protein: round1(item.grams * item.proteinPerGram),
          carbs: round1(item.grams * item.carbsPerGram),
          fat: round1(item.grams * item.fatPerGram),
          fiber: 0,
          sodium: 0,
          sugar: 0,
          confidence: item.confidence,
        })),
      });

      setSavedMessage(`${detectedItems.length} alimento(s) agregado(s) a ${mealTypeLabel(mealType)}.`);
      resetAnalysis();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="add-meal-form">
      <div className="segmented-control">
        <button
          type="button"
          className={`segmented-control__option${entryMode === 'search' ? ' segmented-control__option--active' : ''}`}
          onClick={() => switchEntryMode('search')}
        >
          Buscar
        </button>
        <button
          type="button"
          className={`segmented-control__option${entryMode === 'photo' ? ' segmented-control__option--active' : ''}`}
          onClick={() => switchEntryMode('photo')}
        >
          Foto con IA
        </button>
      </div>

      {entryMode === 'search' ? (
        <>
          <label className="add-meal-form__search">
            <input
              type="text"
              placeholder="Buscar alimento (ej. pollo, arroz, banana)…"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setSelectedFood(null);
                setSavedMessage(null);
              }}
            />
          </label>

          {results.length > 0 && !selectedFood ? (
            <ul className="add-meal-form__results">
              {results.map((food) => (
                <li key={food.id}>
                  <button type="button" onClick={() => selectFood(food)}>
                    {food.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {selectedFood && preview ? (
            <div className="add-meal-form__details">
              <div className="add-meal-form__row">
                <label>
                  Gramos
                  <input type="number" min={0} value={grams} onChange={(event) => setGrams(Number(event.target.value) || 0)} />
                </label>
                <label>
                  Comida
                  <select value={mealType} onChange={(event) => setMealType(event.target.value as MealType)}>
                    {MEAL_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="add-meal-form__photo">
                {mode === 'cloudflare' ? 'Adjuntar foto (opcional, se sube a R2)' : 'Adjuntar foto (opcional, solo vista previa en modo demo)'}
                <input type="file" accept="image/*" onChange={(event) => handlePhotoChange(event.target.files?.[0] ?? null)} />
              </label>
              {photoPreviewUrl ? <img className="add-meal-form__photo-preview" src={photoPreviewUrl} alt="" /> : null}

              <div className="add-meal-form__preview">
                <span>{preview.calories} kcal</span>
                <span>{preview.protein} g prot.</span>
                <span>{preview.carbs} g carb.</span>
                <span>{preview.fat} g grasa</span>
              </div>

              <div className="add-meal-form__actions">
                <button type="button" className="primary-button" onClick={handleSaveSearch} disabled={isSaving}>
                  {isSaving ? 'Guardando…' : 'Guardar'}
                </button>
                <button type="button" onClick={resetSearch} disabled={isSaving}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="add-meal-form__details">
          {mode !== 'cloudflare' ? (
            <p className="hint-text">
              El analisis por IA necesita el backend real: elegi "Cloudflare (D1)" en Perfil primero.
            </p>
          ) : (
            <>
              <label className="add-meal-form__photo">
                Foto de la comida
                <input type="file" accept="image/*" onChange={(event) => handleAnalysisFileChange(event.target.files?.[0] ?? null)} />
              </label>
              {analysisPreviewUrl ? <img className="add-meal-form__photo-preview" src={analysisPreviewUrl} alt="" /> : null}

              {!detectedItems ? (
                <button type="button" className="primary-button" onClick={handleAnalyze} disabled={!analysisFile || isAnalyzing}>
                  {isAnalyzing ? 'Analizando…' : 'Analizar con IA'}
                </button>
              ) : (
                <>
                  <p className="hint-text">
                    Estimaciones de la IA: revisa y corregi antes de guardar. Nunca son medidas exactas.
                  </p>
                  <ul className="detected-items-list">
                    {detectedItems.map((item, index) => (
                      <li key={index} className="detected-items-list__item">
                        <div className="add-meal-form__row">
                          <label>
                            Alimento
                            <input type="text" value={item.name} onChange={(event) => updateDetectedName(index, event.target.value)} />
                          </label>
                          <label>
                            Gramos
                            <input
                              type="number"
                              min={0}
                              value={item.grams}
                              onChange={(event) => updateDetectedGrams(index, Number(event.target.value) || 0)}
                            />
                          </label>
                        </div>
                        <div className="add-meal-form__preview">
                          <span>{Math.round(item.grams * item.caloriesPerGram)} kcal</span>
                          <span>{round1(item.grams * item.proteinPerGram)} g prot.</span>
                          <span>{round1(item.grams * item.carbsPerGram)} g carb.</span>
                          <span>{round1(item.grams * item.fatPerGram)} g grasa</span>
                          <span>{Math.round(item.confidence * 100)}% confianza</span>
                        </div>
                        <button type="button" onClick={() => removeDetectedItem(index)}>
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>

                  <label>
                    Comida
                    <select value={mealType} onChange={(event) => setMealType(event.target.value as MealType)}>
                      {MEAL_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="add-meal-form__actions">
                    <button
                      type="button"
                      className="primary-button"
                      onClick={handleSaveDetected}
                      disabled={isSaving || detectedItems.length === 0}
                    >
                      {isSaving ? 'Guardando…' : 'Confirmar y guardar'}
                    </button>
                    <button type="button" onClick={resetAnalysis} disabled={isSaving}>
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {error ? <p className="debug-error">{error}</p> : null}
      {savedMessage ? <p className="add-meal-form__success">{savedMessage}</p> : null}
    </div>
  );
}
